import { Injectable } from '@angular/core';
import { BaseComponentResolver } from '@services/_base/base-component-resolver/base-component-resolver';
import {
  FileFileData,
  MediaAudioData,
  MediaRemoteVideoData,
  MediaVideoData,
  ParagraphImageData,
  ParagraphSliderItemData,
} from '@generated/content';
import { CmsComponentConfig, CmsComponentNodeData, TMediaNodeType } from '@models/interfaces';
import { Media } from '@interfaces/config/cms-components/media-config';
import { CmsMediaTypeEnum, MediaTypesEnum } from '@models/enums';
import { YoutubeVideo } from '@models/classes';

@Injectable({
  providedIn: 'root',
})
export class MediaComponentResolver extends BaseComponentResolver {
  static override instance: MediaComponentResolver;
  private readonly componentType = Media.TypeEnum;

  static getInstance(): MediaComponentResolver {
    return (MediaComponentResolver.instance ||= new MediaComponentResolver());
  }

  public override resolveComponentConfig(
    node: ParagraphImageData | ParagraphSliderItemData,
    nodePageData: CmsComponentNodeData[]
  ): CmsComponentConfig<Media.ComponentConfig> | null {
    this.nodePageData = nodePageData;

    if (this.isParagraphImageData(node)) {
      const paragraphImageData = node as ParagraphImageData;

      if (!paragraphImageData?.relationships?.field_image?.data) {
        return null;
      }

      const { type, id } = paragraphImageData.relationships.field_image.data;
      const fullWidth = paragraphImageData.attributes?.field_fullwidth ?? false;

      return this.resolveConfig(id, type as TMediaNodeType, fullWidth);
    }

    if (this.isParagraphSliderItemData(node)) {
      const paragraphSliderItemData = node as ParagraphSliderItemData;

      if (!paragraphSliderItemData?.relationships?.field_slider_media?.data) {
        return null;
      }

      const { type, id } = paragraphSliderItemData.relationships.field_slider_media.data;
      const fullWidth = false;

      return this.resolveConfig(id, type as TMediaNodeType, fullWidth);
 
    }

    return null;
  }

  private resolveConfig(nodeId: string, type: TMediaNodeType, fullWidth: boolean): CmsComponentConfig<Media.ComponentConfig> {
    switch (type) {
      case CmsMediaTypeEnum.Audio:
        return this.resolveMediaAudioConfig(nodeId);

      case CmsMediaTypeEnum.Image:
        return this.resolveMediaImageConfig(nodeId, type, fullWidth);

      case CmsMediaTypeEnum.Video:
        return this.resolveMediaVideoConfig(nodeId, fullWidth);

      case CmsMediaTypeEnum.RemoteVideo:
        return this.resolveMediaRemoteVideoConfig(nodeId, fullWidth);

      default:
        return null;
    }
  }

  public resolveMediaAudioConfig(nodeId: string): CmsComponentConfig<Media.ComponentConfig> {
    const mediaAudioNode = this.getNode<MediaAudioData>(nodeId, CmsMediaTypeEnum.Audio);

    if (!mediaAudioNode?.relationships?.field_media_audio_file?.data?.id) {
      return null;
    }

    const fileNode = this.getNode<FileFileData>(
      mediaAudioNode?.relationships.field_media_audio_file.data.id,
      FileFileData.TypeEnum.FileFile
    );

    const url = fileNode?.attributes?.uri?.url;

    if (!url) {
      return null;
    }

    return {
      componentType: this.componentType,
      config: {
        nodeId: BaseComponentResolver.getNodeId(nodeId),
        url: fileNode.attributes.uri.url,
        type: MediaTypesEnum.AUDIO,
        metas: {
          title: mediaAudioNode.attributes.field_title ?? 'Podcast',
          description: mediaAudioNode.attributes.field_description ?? null,
        },
      },
    };
  }

  private resolveMediaImageConfig(
    nodeId: string,
    type: TMediaNodeType,
    fullWidth: boolean = false
  ): CmsComponentConfig<Media.ComponentConfig> {
    return {
      componentType: this.componentType,
      config: {
        nodeId: BaseComponentResolver.getNodeId(nodeId),
        imageConfig: this.getMediaDataUrls(nodeId, type, this.nodePageData, true) || {},
        type: MediaTypesEnum.IMAGE,
        metas: null,
        fullWidth,
      },
    };
  }

  private resolveMediaVideoConfig(nodeId: string, fullWidth: boolean = false): CmsComponentConfig<Media.ComponentConfig> {
    const mediaVideoNode = this.getNode<MediaVideoData>(nodeId, CmsMediaTypeEnum.Video);
    const thumbnailNodeId = mediaVideoNode?.relationships?.thumbnail?.data?.id ?? null;

    const fileNode = this.getNode<FileFileData>(
      mediaVideoNode?.relationships?.field_media_video_file?.data?.id,
      FileFileData.TypeEnum.FileFile
    );

    const url = fileNode.attributes?.uri?.url;

    if (!url) {
      return null;
    }

    return {
      componentType: this.componentType,
      config: {
        nodeId: BaseComponentResolver.getNodeId(nodeId),
        url: fileNode.attributes.uri.url,
        type: MediaTypesEnum.VIDEO,
        fullWidth,
        metas: null,
        thumbnailNodeId,
      },
    };
  }

  private resolveMediaRemoteVideoConfig(nodeId: string, fullWidth: boolean = false): CmsComponentConfig<Media.ComponentConfig> {
    const mediaRemoteVideoNode = this.getNode<MediaRemoteVideoData>(nodeId, CmsMediaTypeEnum.RemoteVideo);

    const mediaYoutubeVideoConfig = new YoutubeVideo(mediaRemoteVideoNode).getComponentConfig();
    const videoId = mediaYoutubeVideoConfig?.videoId;

    if (!videoId) {
      return null;
    }

    return {
      componentType: this.componentType,
      config: {
        nodeId: BaseComponentResolver.getNodeId(nodeId),
        url: null,
        youtubeVideoId: videoId,
        type: MediaTypesEnum.YOUTUBE_VIDEO,
        metas: null,
        fullWidth,
      },
    };
  }

  private isParagraphImageData(node: ParagraphImageData | ParagraphSliderItemData): boolean {
    if (!node) {
      return false;
    }

    return 'field_image' in node.relationships;
  }

  private isParagraphSliderItemData(node: ParagraphImageData | ParagraphSliderItemData): boolean {
    if (!node) {
      return false;
    }

    return 'field_slider_media' in node.relationships;
  }
}
